import { useQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import { VonqContractPostingRequirements } from 'api-clients/monolith/models/VonqContractPostingRequirements';
import { useEffect, useState } from 'react';
import { Control, FieldPath } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { FormAutocompleteCombobox } from '@/components/ui/combobox';
import { FormSelect } from '@/components/ui/select';
import { queryKeys } from '@/query-keys';
import { stripVonq } from '@/utils/strip-vonq';

import { JobPostingForm } from '../../types';
import { VonqDependentAutocompleteInput } from './dependent-autocomplete-input';
import { mapOptions } from './option-helpers';

type FieldDef = VonqContractPostingRequirements['posting_requirements'][number];
type AutocompletePattern = 'none' | 'lazy-load' | 'user-search' | 'dependent';

export function detectAutocompletePattern(
  autocomplete: FieldDef['autocomplete'],
): AutocompletePattern {
  if (autocomplete === null) return 'none';
  // A field is dependent only when parameters_source has an actual term
  // mapping pointing to a sibling facet or vacancy field. The API may return
  // an empty object {} when there is no dependency, so checking for != null
  // alone is not sufficient.
  if (autocomplete.parameters_source?.term !== undefined) return 'dependent';
  // 'credentials' in required_parameters is auto-handled by the contract
  // endpoint — only 'term' determines whether the user needs to type.
  if (autocomplete.required_parameters?.includes('term')) return 'user-search';
  return 'lazy-load';
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

interface VonqAutocompleteInputProps {
  field: FieldDef;
  contractId: string;
  openingId: string;
  name: FieldPath<JobPostingForm>;
  control: Control<JobPostingForm>;
  errorMessage: string | undefined;
  isMultiSelect?: boolean;
}

/**
 * Handles AUTOCOMPLETE, SELECT, and HIER posting requirement fields.
 *
 * Detects which of the four VONQ autocomplete patterns applies to the field,
 * fires the right API calls, and renders the appropriate input component:
 *
 *   Pattern 1 (lazy-load)   — options fetched on mount, rendered as a searchable
 *                             FormSelect that filters the loaded list client-side
 *   Pattern 2 (user-search) — debounced API per keystroke, rendered as FormAutocompleteCombobox
 *   Pattern 3/4 (dependent) — delegated to VonqDependentAutocompleteInput, which
 *                             fetches options from sibling-facet and/or vacancy
 *                             field values (see dependent-autocomplete-input.tsx)
 *   Pattern none            — static field.options; AUTOCOMPLETE uses client-side search
 *
 * For HIER fields the options (whether static or API-fetched) are flattened via
 * mapOptions/flattenHierOptions before being passed to whichever component handles
 * the pattern.
 */
export function VonqAutocompleteInput({
  field,
  contractId,
  openingId,
  name,
  control,
  errorMessage,
  isMultiSelect = false,
}: VonqAutocompleteInputProps) {
  const intl = useIntl();
  const isHier = field.type === 'HIER';
  const pattern = detectAutocompletePattern(field.autocomplete);

  // `query` doubles as the combobox display value:
  //   - while typing: the search string sent to the API
  //   - after selection: the chosen option's label
  //   - after clear: reset to ''
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);

  const staticOptions = mapOptions(field.options, isHier);

  // Pattern 1: lazy-load all options on mount (no search term required).
  // Try an empty body {}; some facets return 400 for an empty term — those
  // surface an empty list rather than crashing.
  const { data: lazyData, isFetching: lazyFetching } = useQuery({
    queryKey: queryKeys.jobPosting.vonqAutocomplete.lazy(
      contractId,
      field.name,
    ),
    queryFn: async () => {
      try {
        return await SourcingService.postInternalApiSourcingVonqContractsPostingRequirementsAutocomplete(
          contractId,
          field.name,
          {},
        );
      } catch {
        return { options: [] };
      }
    },
    enabled: pattern === 'lazy-load',
    staleTime: 5 * 60_000,
  });

  // Pattern 2: debounced search-as-you-type.
  const { data: searchData, isFetching: searchFetching } = useQuery({
    queryKey: queryKeys.jobPosting.vonqAutocomplete.search(
      contractId,
      field.name,
      debouncedQuery,
    ),
    queryFn: () =>
      SourcingService.postInternalApiSourcingVonqContractsPostingRequirementsAutocomplete(
        contractId,
        field.name,
        { term: debouncedQuery },
      ),
    enabled: pattern === 'user-search' && debouncedQuery.length > 0,
    staleTime: 30_000,
  });

  const rawApiOptions =
    pattern === 'lazy-load'
      ? lazyData?.options
      : pattern === 'user-search'
        ? searchData?.options
        : [];

  const apiOptions = mapOptions(rawApiOptions, isHier);

  if (pattern === 'user-search') {
    return (
      <FormAutocompleteCombobox
        name={name}
        control={control}
        label={stripVonq(field.label)}
        required={field.required}
        error={errorMessage}
        options={apiOptions}
        query={query}
        setQuery={setQuery}
        isLoading={searchFetching && debouncedQuery.length > 0}
        isClearable
        placeholder={intl.formatMessage({
          defaultMessage: 'Search and select',
          description:
            'Placeholder for a VONQ posting requirement field with API-backed inline search',
        })}
      />
    );
  }

  if (pattern === 'lazy-load') {
    // Options are already fully loaded, so search filters the in-memory list
    // client-side. FormSelect renders its search box whenever setQuery is
    // passed and delegates filtering to us (shouldFilter={false}).
    const filteredOptions = query
      ? apiOptions.filter(opt =>
          opt.label.toLowerCase().includes(query.toLowerCase()),
        )
      : apiOptions;

    return (
      <FormSelect
        name={name}
        control={control}
        label={stripVonq(field.label)}
        required={field.required}
        error={errorMessage}
        isMultiSelect={isMultiSelect}
        options={filteredOptions}
        isLoading={lazyFetching}
        query={query}
        setQuery={q => setQuery(q ?? '')}
        isClearable
        placeholder={
          isMultiSelect
            ? intl.formatMessage({
                defaultMessage: 'Select options',
                description:
                  'Placeholder for a multi-select VONQ posting requirement field whose options are loaded from the API',
              })
            : intl.formatMessage({
                defaultMessage: 'Select an option',
                description:
                  'Placeholder for a VONQ posting requirement field whose options are loaded from the API',
              })
        }
      />
    );
  }

  if (pattern === 'dependent') {
    // Pattern 3 (dependent facet) & Pattern 4 (vacancy field): options are
    // fetched using sibling-facet values and/or a vacancy field (e.g. the job
    // title) as the term. Delegated to a dedicated component so its watch/query/
    // reset hooks run unconditionally.
    return (
      <VonqDependentAutocompleteInput
        field={field}
        contractId={contractId}
        openingId={openingId}
        name={name}
        control={control}
        errorMessage={errorMessage}
        isMultiSelect={isMultiSelect}
      />
    );
  }

  // Pattern none — static options from field.options.
  // Only SELECT / HIER fields with autocomplete: null reach this branch.
  // AUTOCOMPLETE fields always have a non-null autocomplete property so they
  // are always handled by one of the branches above.
  const filteredStaticOptions = query
    ? staticOptions.filter(opt =>
        opt.label.toLowerCase().includes(query.toLowerCase()),
      )
    : staticOptions;

  return (
    <FormSelect
      name={name}
      control={control}
      label={field.label}
      required={field.required}
      error={errorMessage}
      isMultiSelect={false}
      options={filteredStaticOptions}
      query={query}
      setQuery={q => setQuery(q ?? '')}
      isClearable
      placeholder={intl.formatMessage({
        defaultMessage: 'Select an option',
        description:
          'Placeholder for a VONQ SELECT or HIER posting requirement dropdown with static options',
      })}
    />
  );
}
